import { standardFontDataUrl } from '../../lib/pdfFonts.js';

/**
 * Section 3 — a PDF's pages as pictures, for the summary's thumbnail and the
 * enlarged view of an invoice. Drawn here, on the device, from the file that was
 * picked: nothing is stored yet, so there is no server copy to show.
 *
 * pdf.js is loaded on the first call, as `PdfPreview` does, so it lands only on
 * whoever picks a PDF. A file that cannot be drawn gives no pages, and the
 * screen shows the file's icon in their place.
 */

export interface PdfPages {
  pages: string[];
  /** Original one-based page numbers, matching the rendered images. */
  pageNumbers: number[];
  total: number;
}

/** The pages an enlarged view draws, at most: a 40-page statement is not looked at page by page here. */
export const PDF_VIEW_PAGES = 20;

/**
 * One file at a time. pdf.js takes its worker from a global, so two files drawn
 * at once would each set it and the first to finish would end the other's.
 */
let queue: Promise<unknown> = Promise.resolve();

function draw(file: Blob, limit: number, width: number, pageNumbers?: readonly number[]): Promise<PdfPages> {
  const selection = pageNumbers ? [...pageNumbers] : undefined;
  const turn = queue.then(() => drawNow(file, limit, width, selection));
  queue = turn.catch(() => undefined);
  return turn;
}

async function drawNow(file: Blob, limit: number, width: number, pageNumbers?: readonly number[]): Promise<PdfPages> {
  const bytes = new Uint8Array(await file.arrayBuffer());
  const [{ getDocument, GlobalWorkerOptions }, { default: PdfWorker }] = await Promise.all([
    import('pdfjs-dist/legacy/build/pdf.mjs'),
    import('pdfjs-dist/legacy/build/pdf.worker.min.mjs?worker'),
  ]);
  const worker = new PdfWorker();
  GlobalWorkerOptions.workerPort = worker;
  const task = getDocument({ data: bytes, standardFontDataUrl });
  try {
    const pdf = await task.promise;
    const pages: string[] = [];
    const selected = pageNumbers === undefined ? null : [...new Set(pageNumbers.filter((number) => Number.isInteger(number) && number >= 1 && number <= pdf.numPages))].sort((a, b) => a - b);
    const numbers = selected?.slice(0, Math.min(limit, PDF_VIEW_PAGES)) ?? Array.from({ length: Math.min(limit, PDF_VIEW_PAGES, pdf.numPages) }, (_, index) => index + 1);
    const drawnNumbers: number[] = [];
    for (const number of numbers) {
      const page = await pdf.getPage(number);
      const unscaled = page.getViewport({ scale: 1 });
      const viewport = page.getViewport({ scale: width / unscaled.width });
      const canvas = document.createElement('canvas');
      canvas.width = Math.floor(viewport.width);
      canvas.height = Math.floor(viewport.height);
      const context = canvas.getContext('2d');
      if (!context) break;
      await page.render({ canvas, canvasContext: context, viewport }).promise;
      pages.push(canvas.toDataURL('image/jpeg', 0.75));
      drawnNumbers.push(number);
      page.cleanup();
    }
    return { pages, pageNumbers: drawnNumbers, total: selected?.length ?? pdf.numPages };
  } finally {
    void task.destroy();
    worker.terminate();
  }
}

const firstPages = new WeakMap<Blob, Map<string, Promise<string | null>>>();
const viewedPages = new WeakMap<Blob, Map<string, Promise<PdfPages>>>();
const selectionKey = (pageNumbers?: readonly number[]) => pageNumbers === undefined ? '*' : pageNumbers.join(',');

/** The first page, small, once per file however often its row is drawn. */
export function pdfThumb(file: Blob, pageNumbers?: readonly number[]): Promise<string | null> {
  let selections = firstPages.get(file);
  if (!selections) { selections = new Map(); firstPages.set(file, selections); }
  const key = selectionKey(pageNumbers);
  let made = selections.get(key);
  if (!made) {
    made = draw(file, 1, 240, pageNumbers).then(
      ({ pages }) => pages[0] ?? null,
      () => null,
    );
    selections.set(key, made);
  }
  return made;
}

/** Every page up to `PDF_VIEW_PAGES`, wide enough to read. */
export function pdfPages(file: Blob, pageNumbers?: readonly number[]): Promise<PdfPages> {
  let selections = viewedPages.get(file);
  if (!selections) { selections = new Map(); viewedPages.set(file, selections); }
  const key = selectionKey(pageNumbers);
  let made = selections.get(key);
  if (!made) {
    made = draw(file, PDF_VIEW_PAGES, 900, pageNumbers).catch(() => ({ pages: [], pageNumbers: [], total: 0 }));
    selections.set(key, made);
  }
  return made;
}
